// Requêtes partagées (TanStack Query). Toutes les clés commencent par le domaine ; le
// changement de contexte vide l'ensemble du cache (FournisseurSession).
import { useQuery } from '@tanstack/react-query';
import { requete } from './client';
import type { Organisation, Delegation, Liste, ModuleEtat, Role, Utilisateur } from './types';
import { useSession } from '../session/FournisseurSession';
import { peut } from '../session/permissions';

export function useModules() {
  const { contexte } = useSession();
  return useQuery({
    queryKey: ['modules'],
    queryFn: ({ signal }) => requete<Liste<ModuleEtat>>('/modules', { signal }).then((r) => r.elements),
    enabled: peut(contexte, 'parametres', 'voir'),
    staleTime: 60_000,
  });
}

export function useOrganisation() {
  const { contexte } = useSession();
  return useQuery({
    queryKey: ['organisation'],
    queryFn: ({ signal }) => requete<Organisation>('/organisations/courante', { signal }),
    enabled: Boolean(contexte?.appartenance_active?.organisation_id),
    staleTime: 5 * 60_000,
  });
}

export function useUtilisateurs(actif = true) {
  const { contexte } = useSession();
  return useQuery({
    queryKey: ['utilisateurs'],
    queryFn: ({ signal }) => requete<Liste<Utilisateur>>('/utilisateurs', { signal }).then((r) => r.elements),
    enabled: actif && peut(contexte, 'utilisateurs', 'voir'),
  });
}

export function useRoles(actif = true) {
  const { contexte } = useSession();
  return useQuery({
    queryKey: ['roles'],
    queryFn: ({ signal }) => requete<Liste<Role>>('/roles', { signal }).then((r) => r.elements),
    enabled: actif && peut(contexte, 'utilisateurs', 'voir'),
    staleTime: 60_000,
  });
}

export function useDelegations() {
  const { contexte } = useSession();
  return useQuery({
    queryKey: ['delegations'],
    queryFn: ({ signal }) => requete<Liste<Delegation>>('/delegations', { signal }).then((r) => r.elements),
    enabled: Boolean(contexte?.appartenance_active),
  });
}

/** Libellé d'une personne à partir de son appartenance (si la liste des utilisateurs est lisible). */
export function nomAppartenance(utilisateurs: Utilisateur[] | undefined, appartenanceId: string, moi?: string): string {
  if (moi && appartenanceId === moi) return 'Vous';
  const u = utilisateurs?.find((x) => x.appartenance_id === appartenanceId);
  return u ? [u.prenoms, u.nom].filter(Boolean).join(' ') : `Appartenance ${appartenanceId.slice(0, 8)}`;
}
